  if (!customElements.get('cart-upsell')) {
    class CartUpsell extends HTMLElement {
      constructor() {
        super();
        this.track = this.querySelector('[data-cart-upsell-track]');
        this.message = this.querySelector('[data-cart-upsell-message]');
        this.details = this.querySelector('[data-cart-upsell-details]');
        this.limit = this.dataset.limit || '4';
        this.sectionId = this.dataset.sectionId || 'cart-drawer-upsell';
        this.recommendationsUrl = this.dataset.recommendationsUrl || '';
        this.defaultButtonText = this.dataset.buttonText || '+ Add';
        this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
        this.loading = false;
        this.refreshQueued = false;
        this.refreshForce = false;
        this.retryTimer = null;
        this.refreshCartSnapshot = null;
        this.currentSignature = '';
        this.currentSeedId = '';
        this.currentState = '';
        this.boundRefresh = this.queueRefresh.bind(this);
        this.boundToggle = this.savePanelState.bind(this);
        this.boundClick = this.onClick.bind(this);
        this.boundMenuClick = this.onMenuClick.bind(this);
        this.boundDocumentClick = this.onDocumentClick.bind(this);
        this.boundKeydown = this.onKeydown.bind(this);
        this.boundViewportChange = this.positionSizeMenu.bind(this);
        this.boundApplyStoredPincodeVerification = this.applyStoredPincodeVerification.bind(this);
        this.boundHandlePageShow = this.handlePageShow.bind(this);
        this.rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        this.panelStorageKey = 'cart-upsell-panel-state';
        this.activeSizeControl = null;
        this.activeSizeTrigger = null;
        this.activeSizeMenu = null;
        this.activeSizeMenuInner = null;
        this.activeSizeMenuHost = null;
      }

      connectedCallback() {
        if (!this.track || !this.recommendationsUrl) {
          this.hide();
          return;
        }

        // tt-custom-cart-upsell
        this.drawerComponent = this.closest('cart-drawer-component');
        // tt-custom-cart-upsell
        this.restorePanelState();
        this.addEventListeners();
        this.initSwipe();
        if (this.details) {
          this.details.addEventListener('toggle', this.boundToggle);
        }

        if (this.isCartDrawerOpen()) {
          // tt-custom-cart-upsell
          this.queueRefresh({ type: 'dialog:open' });
          // tt-custom-cart-upsell
        }
      }

      disconnectedCallback() {
        this.closeSizeMenu(false);
        this.clearRetryTimer();
        this.removeEventListeners();
        this.destroySwipe();
        if (this.details) {
          this.details.removeEventListener('toggle', this.boundToggle);
        }
      }

      // tt-custom-cart-upsell
      addEventListeners() {
        // 'cart:update' matches ThemeEvents.cartUpdate in assets/events.js. Not imported:
        // this inline javascript block bundles as a shared classic script, not an ES module.
        document.addEventListener('cart:update', this.boundRefresh);
        this.drawerComponent?.addEventListener('dialog:open', this.boundRefresh);
        document.addEventListener('click', this.boundDocumentClick);
        document.addEventListener('keydown', this.boundKeydown);
        window.addEventListener('resize', this.boundViewportChange, { passive: true });
        window.addEventListener('scroll', this.boundViewportChange, true);
        this.addEventListener('click', this.boundClick);
        document.addEventListener('pincode-gate:verified', this.boundApplyStoredPincodeVerification);
        window.addEventListener('pageshow', this.boundHandlePageShow);

        // window.PincodeGate is set by a *module* script (pincode-gate-dialog.js),
        // while this file is a classic script - independent execution queues
        // with no guaranteed order between them. `refresh()` already re-applies
        // stored verification right after rendering recommendation cards, but
        // `load` is a guaranteed final correction pass in case anything rendered
        // before window.PincodeGate existed yet.
        if (document.readyState === 'complete') {
          this.applyStoredPincodeVerification();
        } else {
          window.addEventListener('load', this.boundApplyStoredPincodeVerification);
        }
      }

      removeEventListeners() {
        document.removeEventListener('cart:update', this.boundRefresh);
        this.drawerComponent?.removeEventListener('dialog:open', this.boundRefresh);
        document.removeEventListener('click', this.boundDocumentClick);
        document.removeEventListener('keydown', this.boundKeydown);
        window.removeEventListener('resize', this.boundViewportChange);
        window.removeEventListener('scroll', this.boundViewportChange, true);
        this.removeEventListener('click', this.boundClick);
        document.removeEventListener('pincode-gate:verified', this.boundApplyStoredPincodeVerification);
        window.removeEventListener('pageshow', this.boundHandlePageShow);
        window.removeEventListener('load', this.boundApplyStoredPincodeVerification);
      }

      // A page can be restored from the browser's back/forward cache instead
      // of a real reload (e.g. hitting Back after checking a pincode on a
      // PDP) - no script re-runs on that restore, so upsell buttons frozen
      // mid-gate would otherwise stay stuck even though localStorage was
      // updated on the page the shopper just came back from.
      handlePageShow(event) {
        if (event.persisted) this.applyStoredPincodeVerification();
      }
      // tt-custom-cart-upsell

      initSwipe() {
        const track = this.track;
        if (!track) return;

        let isDown = false;
        let startX = 0;
        let startScrollLeft = 0;
        let wasDragged = false;

        track.style.cursor = 'grab';

        this._swipeDown = (e) => {
          if (e.pointerType === 'touch') return;
          if (e.target.closest('a, button, input, select, label, [role="button"]')) return;
          isDown = true;
          wasDragged = false;
          startX = e.clientX;
          startScrollLeft = track.scrollLeft;
          track.style.cursor = 'grabbing';
          track.style.scrollSnapType = 'none';
        };

        this._swipeMove = (e) => {
          if (!isDown || e.pointerType === 'touch') return;
          const dx = startX - e.clientX;
          if (!wasDragged && Math.abs(dx) > 4) {
            wasDragged = true;
            track.setPointerCapture(e.pointerId);
          }
          if (wasDragged) track.scrollLeft = startScrollLeft + dx;
        };

        this._swipeUp = (e) => {
          if (!isDown || e.pointerType === 'touch') return;
          isDown = false;
          track.style.cursor = 'grab';
          track.style.scrollSnapType = '';
          if (wasDragged) {
            const card = track.querySelector('.cart-upsell__card, .cart-upsell__skeleton');
            const snapUnit = (card ? card.offsetWidth : track.offsetWidth) + 12;
            const targetIndex = Math.round(track.scrollLeft / snapUnit);
            track.scrollTo({ left: targetIndex * snapUnit, behavior: 'smooth' });
          }
          wasDragged = false;
        };

        track.addEventListener('pointerdown', this._swipeDown);
        track.addEventListener('pointermove', this._swipeMove);
        track.addEventListener('pointerup', this._swipeUp);
        track.addEventListener('pointercancel', this._swipeUp);
      }

      destroySwipe() {
        const track = this.track;
        if (!track || !this._swipeDown) return;
        track.removeEventListener('pointerdown', this._swipeDown);
        track.removeEventListener('pointermove', this._swipeMove);
        track.removeEventListener('pointerup', this._swipeUp);
        track.removeEventListener('pointercancel', this._swipeUp);
        track.style.cursor = '';
      }

      // tt-custom-cart-upsell
      queueRefresh(event) {
        const isDrawerOpen = this.isCartDrawerOpen();
        const isCartUpdate = Boolean(event && event.type === 'cart:update');
        const isDrawerOpenEvent = Boolean(event && event.type === 'dialog:open');
        const cartSnapshot = this.extractCartSnapshot(event);

        if (event && event.detail && event.detail.force) {
          this.refresh(true, 0, cartSnapshot);
          return;
        }

        // Prefer the event's cart snapshot on cart updates. The Shopify cart endpoint can lag
        // the first add-to-cart write, so using the already-fresh event payload avoids an
        // unnecessary cart.js race on the initial session.
        const force = isCartUpdate || (isDrawerOpenEvent && isDrawerOpen);

        if (this.loading) {
          this.refreshQueued = true;
          this.refreshForce = this.refreshForce || force;
          if (cartSnapshot) {
            this.refreshCartSnapshot = cartSnapshot;
          }
          return;
        }

        this.refresh(force, 0, cartSnapshot);
      }
      // tt-custom-cart-upsell

      async refresh(force = false, retryAttempt = 0, cartSnapshot = null) {
        if (this.loading) {
          this.refreshQueued = true;
          this.refreshForce = this.refreshForce || force;
          if (cartSnapshot) {
            this.refreshCartSnapshot = cartSnapshot;
          }
          return;
        }

        if (retryAttempt === 0) {
          this.clearRetryTimer();
        }

        this.loading = true;
        this.classList.add('is-loading');
        this.clearMessage();

        try {
          const cart = cartSnapshot && Array.isArray(cartSnapshot.items) ? cartSnapshot : await this.fetchCart();

          if (!cart || !Array.isArray(cart.items) || !this.getCartItemCount(cart)) {
            this.hide();
            return;
          }

          this.removeAttribute('hidden');
          this.currentState = '';
          this.closeSizeMenu(false);

          const seedIds = [...new Set(cart.items.map((item) => String(item.product_id)).filter(Boolean))];
          if (!seedIds.length) {
            this.closeSizeMenu(false);
            this.track.innerHTML = '';
            this.currentSignature = cart.items.map((item) => String(item.product_id)).join('|');
            this.currentSeedId = '';
            this.currentState = 'empty';
            this.refreshCartSnapshot = null;
            return;
          }

          const cartSignature = cart.items.map((item) => String(item.product_id)).join('|');
          if (!force && this.currentSignature === cartSignature && this.currentState) {
            return;
          }

          const recommendation = await this.findRecommendations(seedIds, cart.items);

          // tt-custom-cart-upsell
          if (!recommendation) {
            if (this.scheduleRetry(retryAttempt, cart)) {
              this.refreshQueued = false;
              this.refreshForce = false;
              return;
            }

            this.hide();
            this.currentSignature = cartSignature;
            this.currentState = 'empty';
            return;
          }
          // tt-custom-cart-upsell

          this.closeSizeMenu(false);
          this.track.innerHTML = recommendation.html;
          this.applyStoredPincodeVerification();
          this.currentSignature = cartSignature;
          this.currentSeedId = recommendation.seedId;
          this.currentState = 'loaded';
          this.refreshCartSnapshot = null;
          this.clearRetryTimer();
        } catch (error) {
          this.hide();
          console.error('Cart upsell failed to load', error);
        } finally {
          this.loading = false;
          if (!this.retryTimer) {
            this.classList.remove('is-loading');
          }

          if (this.refreshQueued) {
            const queuedForce = this.refreshForce;
            const queuedCartSnapshot = this.refreshCartSnapshot;
            this.refreshQueued = false;
            this.refreshForce = false;
            this.refreshCartSnapshot = null;
            this.refresh(queuedForce, 0, queuedCartSnapshot);
          } else if (!this.retryTimer) {
            this.refreshCartSnapshot = null;
          }
        }
      }

      async fetchCart() {
        const response = await fetch(`${this.rootPath}cart.js`, {
          credentials: 'same-origin'
        });

        if (!response.ok) {
          throw new Error('Failed to load cart');
        }

        return response.json();
      }

      // tt-custom-cart-upsell
      async fetchRecommendations(seedId, intent = 'related') {
        const cacheKey = `${seedId}:${this.limit}:${intent}`;

        if (CartUpsell.cache.has(cacheKey)) {
          return CartUpsell.cache.get(cacheKey);
        }

        const url = new URL(this.recommendationsUrl, window.location.origin);
        url.searchParams.set('section_id', this.sectionId);
        url.searchParams.set('product_id', seedId);
        url.searchParams.set('limit', this.limit);
        url.searchParams.set('intent', intent);

        const response = await fetch(url.toString(), {
          credentials: 'same-origin'
        });

        if (!response.ok) {
          throw new Error('Failed to load recommendations');
        }

        const html = await response.text();

        // Only cache responses that actually contain recommendations. An empty result can be
        // a transient miss (e.g. Shopify's recommendation index hasn't caught up yet for a
        // brand-new session/product) rather than a permanent "no matches" answer â€” caching it
        // would lock the widget empty for the rest of the page's lifetime (this Map never
        // clears until a full reload), which is exactly the "works after refresh" symptom.
        if (html.includes('data-cart-upsell-recommendations')) {
          CartUpsell.cache.set(cacheKey, html);
        }

        return html;
      }

      async findRecommendations(seedIds, cartItems) {
        // "related" relies on Shopify's ML model, which can be empty for a small/new
        // catalog. Fall back to "complementary" before giving up on a seed product.
        for (const intent of ['related', 'complementary']) {
          for (const seedId of seedIds) {
            const rawHtml = await this.fetchRecommendations(seedId, intent);
            const filteredHtml = this.filterRecommendationHtml(rawHtml, cartItems);

            if (filteredHtml) {
              return {
                html: filteredHtml,
                seedId: seedId
              };
            }
          }
        }

        return null;
      }
      // tt-custom-cart-upsell

      clearRetryTimer() {
        if (this.retryTimer) {
          window.clearTimeout(this.retryTimer);
          this.retryTimer = null;
        }
      }

      scheduleRetry(retryAttempt, cartSnapshot) {
        const maxRetryAttempts = 2;

        if (retryAttempt >= maxRetryAttempts) {
          return false;
        }

        // The recommendation endpoint can lag briefly after the first cart write.
        const nextAttempt = retryAttempt + 1;
        const delay = Math.min(1200 * nextAttempt, 2400);

        this.retryTimer = window.setTimeout(() => {
          this.retryTimer = null;
          this.refresh(true, nextAttempt, cartSnapshot);
        }, delay);

        return true;
      }

      extractCartSnapshot(event) {
        const resource = event && event.detail ? event.detail.resource : null;

        if (!resource || typeof resource !== 'object' || !Array.isArray(resource.items)) {
          return null;
        }

        return resource;
      }

      getCartItemCount(cart) {
        if (typeof cart.item_count === 'number') {
          return cart.item_count;
        }

        if (!Array.isArray(cart.items)) {
          return 0;
        }

        return cart.items.reduce((total, item) => {
          const quantity = Number(item && item.quantity ? item.quantity : 0);
          return total + (Number.isFinite(quantity) ? quantity : 0);
        }, 0);
      }

      filterRecommendationHtml(html, cartItems) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const container = doc.querySelector('[data-cart-upsell-recommendations]');

        if (!container) {
          return '';
        }

        const cartProductIds = new Set(cartItems.map((item) => String(item.product_id)));
        container.querySelectorAll('[data-product-id]').forEach((card) => {
          if (cartProductIds.has(card.dataset.productId)) {
            card.remove();
          }
        });

        if (!container.children.length) {
          return '';
        }

        return container.innerHTML;
      }

      // tt-custom-cart-upsell
      isCartDrawerOpen() {
        const dialog = this.closest('.cart-drawer__dialog');
        return Boolean(dialog && dialog.open);
      }
      // tt-custom-cart-upsell

      onClick(event) {
        const sizeTrigger = event.target.closest('[data-cart-upsell-size-trigger]');

        if (sizeTrigger && this.contains(sizeTrigger)) {
          event.preventDefault();
          event.stopPropagation();
          this.toggleSizeMenu(sizeTrigger.closest('[data-cart-upsell-size-control]'));
          return;
        }

        const sizeOption = event.target.closest('[data-cart-upsell-size-option]');

        if (sizeOption && this.contains(sizeOption)) {
          event.preventDefault();
          event.stopPropagation();
          this.selectSizeOption(sizeOption);
          return;
        }

        const button = event.target.closest('[data-cart-upsell-add]');

        if (!button || button.disabled || !this.contains(button)) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.closeSizeMenu(false);

        if (button.dataset.pincodeGate === 'true' && button.dataset.pincodeVerified !== 'true') {
          this.openPincodeGate(button);
          return;
        }

        this.addItem(button);
      }

      // tt-custom-cart-upsell
      openPincodeGate(button) {
        const dialog = document.getElementById('pincode-gate-dialog');
        if (!dialog || typeof dialog.open !== 'function') {
          // Dialog unavailable for some reason - fail closed rather than skip the gate.
          return;
        }

        // No per-button onValidated needed: the dialog broadcasts
        // 'pincode-gate:verified' on success (assets/pincode-gate-dialog.js),
        // which this component listens for and re-runs
        // applyStoredPincodeVerification() below - unlocking this button and
        // every other gated button already in the track, not just this one.
        dialog.open();
      }

      // A pincode verified via the popup on any earlier product is
      // remembered in localStorage (see assets/pincode-gate.js), so newly
      // rendered gated cards should start already unlocked instead of
      // forcing a re-check on every product. Runs both ways: also re-locks
      // already-unlocked buttons if a later, different pincode fails and
      // clears that stored verification (see clearVerifiedPincode), so this
      // track never keeps trusting a stale success.
      applyStoredPincodeVerification() {
        const verified = Boolean(window.PincodeGate?.getVerifiedPincode?.());
        const gatedButtons = this.track?.querySelectorAll('[data-pincode-gate="true"]') || [];
        gatedButtons.forEach((button) => {
          if (verified) {
            button.dataset.pincodeVerified = 'true';
            button.textContent = button.dataset.defaultLabel || '+ Add';
          } else {
            button.dataset.pincodeVerified = 'false';
            button.textContent = 'Check Availability';
          }
        });
      }
      // tt-custom-cart-upsell

      removeAddedCard(button) {
        const card = button.closest('.cart-upsell__card');

        if (!card) {
          return;
        }

        if (this.activeSizeControl && card.contains(this.activeSizeControl)) {
          this.closeSizeMenu(false);
        }

        card.remove();

        if (!this.querySelector('.cart-upsell__card')) {
          this.hide();
          return;
        }
      }

      toggleSizeMenu(control) {
        if (!control) {
          return;
        }

        if (this.activeSizeControl === control) {
          this.closeSizeMenu(false);
          return;
        }

        this.openSizeMenu(control);
      }

      openSizeMenu(control) {
        const trigger = control.querySelector('[data-cart-upsell-size-trigger]');
        const menu = control.querySelector('[data-cart-upsell-size-menu]');

        if (!trigger || !menu || !control.querySelector('[data-cart-upsell-size-option]')) {
          return;
        }

        this.closeSizeMenu(false);
        this.activeSizeControl = control;
        this.activeSizeTrigger = trigger;
        this.activeSizeMenu = menu;
        this.activeSizeMenuInner = menu.querySelector('.cart-upsell__size-menu-inner');

        trigger.setAttribute('aria-expanded', 'true');
        const rootStyles = window.getComputedStyle(this);
        menu.style.setProperty('--cart-upsell-button-border', rootStyles.getPropertyValue('--cart-upsell-button-border').trim());
        menu.style.setProperty('--cart-upsell-text', rootStyles.getPropertyValue('--cart-upsell-text').trim());
        menu._cartUpsellControl = control;
        menu._cartUpsellCard = control.closest('.cart-upsell__card');
        menu._cartUpsellOriginalParent = menu.parentNode;
        menu._cartUpsellNextSibling = menu.nextSibling;
        // tt-custom-cart-upsell
        // The cart drawer is a native <dialog> opened with showModal(), which renders
        // it in the browser's top layer — above all regular DOM content regardless of
        // z-index. Portaling the menu to document.body (a non-top-layer node) put it
        // behind the drawer, so it opened but was invisible. Portal into the dialog
        // itself instead, so it stays inside that top-layer context.
        const topLayerHost = this.closest('dialog') || document.body;
        if (topLayerHost && menu.parentNode !== topLayerHost) {
          topLayerHost.appendChild(menu);
        }
        this.activeSizeMenuHost = topLayerHost;
        // tt-custom-cart-upsell
        menu.addEventListener('click', this.boundMenuClick);
        menu.hidden = false;
        menu.style.visibility = 'hidden';
        menu.style.overflow = 'hidden';
        if (this.activeSizeMenuInner) {
          this.activeSizeMenuInner.style.overflowY = 'auto';
        }

        requestAnimationFrame(() => {
          this.positionSizeMenu();

          if (this.activeSizeMenu === menu) {
            menu.style.visibility = '';
          }
        });
      }

      closeSizeMenu(focusTrigger = false) {
        const trigger = this.activeSizeTrigger;
        const menu = this.activeSizeMenu;

        if (!trigger && !menu) {
          return;
        }

        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
        }

        if (menu) {
          menu.removeEventListener('click', this.boundMenuClick);
          menu.hidden = true;
          menu.style.visibility = '';
          menu.style.left = '';
          menu.style.top = '';
          menu.style.width = '';
          menu.style.maxHeight = '';
          menu.style.overflow = '';
          menu.removeAttribute('data-open-below');
          menu.style.removeProperty('--cart-upsell-button-border');
          menu.style.removeProperty('--cart-upsell-text');

          if (this.activeSizeMenuInner) {
            this.activeSizeMenuInner.style.maxHeight = '';
            this.activeSizeMenuInner.style.overflowY = '';
          }

          const originalParent = menu._cartUpsellOriginalParent;
          const nextSibling = menu._cartUpsellNextSibling;

          if (originalParent && originalParent.isConnected) {
            if (nextSibling && nextSibling.parentNode === originalParent) {
              originalParent.insertBefore(menu, nextSibling);
            } else {
              originalParent.appendChild(menu);
            }
          } else if (menu.parentNode) {
            menu.parentNode.removeChild(menu);
          }

          menu._cartUpsellOriginalParent = null;
          menu._cartUpsellNextSibling = null;
          menu._cartUpsellControl = null;
          menu._cartUpsellCard = null;
        }

        this.activeSizeControl = null;
        this.activeSizeTrigger = null;
        this.activeSizeMenu = null;
        this.activeSizeMenuInner = null;
        this.activeSizeMenuHost = null;

        if (focusTrigger && trigger && typeof trigger.focus === 'function') {
          try {
            trigger.focus({ preventScroll: true });
          } catch (error) {
            trigger.focus();
          }
        }
      }

      positionSizeMenu() {
        if (!this.activeSizeControl || !this.activeSizeTrigger || !this.activeSizeMenu || this.activeSizeMenu.hidden) {
          return;
        }

        const triggerRect = this.activeSizeTrigger.getBoundingClientRect();
        const menu = this.activeSizeMenu;
        const menuInner = this.activeSizeMenuInner || menu.querySelector('.cart-upsell__size-menu-inner');
        const viewportPadding = 8;
        const gap = 6;
        const viewportWidth = Math.max(window.innerWidth, 0);
        const viewportHeight = Math.max(window.innerHeight, 0);
        const menuWidth = Math.min(Math.max(Math.round(triggerRect.width), 0), Math.max(viewportWidth - viewportPadding * 2, 0));
        menu.style.width = `${menuWidth}px`;

        const availableBelow = Math.max(0, viewportHeight - triggerRect.bottom - gap - viewportPadding);
        const innerMaxHeight = Math.max(0, Math.floor(availableBelow - 8));

        menu.style.maxHeight = '';
        menu.style.overflow = 'hidden';
        if (menuInner) {
          menuInner.style.maxHeight = `${innerMaxHeight}px`;
          menuInner.style.overflowY = 'auto';
        }

        const top = triggerRect.bottom + gap;
        let left = triggerRect.left;
        const maxLeft = viewportWidth - viewportPadding - menuWidth;

        if (left < viewportPadding) {
          left = viewportPadding;
        } else if (left > maxLeft) {
          left = Math.max(viewportPadding, maxLeft);
        }

        // tt-custom-cart-upsell
        // The menu is portaled into the cart drawer <dialog> (see openSizeMenu) so it
        // stays in the same top-layer stacking context. But once the drawer's open
        // animation finishes, base.css leaves a `transform` on the dialog via an
        // animation with fill-mode "forwards" — and per spec, any transformed ancestor
        // becomes the containing block for its position:fixed descendants. That means
        // this "fixed" menu is actually positioned relative to the dialog's box, not
        // the viewport, even though triggerRect above is viewport-relative. Subtract
        // the host's own viewport offset so the two coordinate spaces line back up.
        const host = this.activeSizeMenuHost;
        let hostLeft = 0;
        let hostTop = 0;
        if (host && host !== document.body) {
          const hostRect = host.getBoundingClientRect();
          hostLeft = hostRect.left;
          hostTop = hostRect.top;
        }
        // tt-custom-cart-upsell

        menu.style.left = `${Math.round(left - hostLeft)}px`;
        menu.style.top = `${Math.round(top - hostTop)}px`;
        menu.dataset.openBelow = 'true';
      }

      onMenuClick(event) {
        if (!this.activeSizeMenu || !this.activeSizeMenu.contains(event.target)) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        const sizeOption = event.target.closest('[data-cart-upsell-size-option]');

        if (sizeOption) {
          this.selectSizeOption(sizeOption);
        }
      }

      selectSizeOption(option) {
        const menu = option.closest('[data-cart-upsell-size-menu]');
        const control = menu && menu._cartUpsellControl ? menu._cartUpsellControl : option.closest('[data-cart-upsell-size-control]');
        const card = menu && menu._cartUpsellCard ? menu._cartUpsellCard : option.closest('.cart-upsell__card');

        if (!control || !card) {
          return;
        }

        const trigger = control.querySelector('[data-cart-upsell-size-trigger]');
        const valueEl = control.querySelector('[data-cart-upsell-size-value]');
        const addButton = card.querySelector('[data-cart-upsell-add]');
        const sizeValue = option.dataset.sizeValue || option.textContent.trim();
        const optionScope = menu || control;

        if (optionScope) {
          optionScope.querySelectorAll('[data-cart-upsell-size-option]').forEach((sizeOption) => {
            const isSelected = sizeOption === option;
            sizeOption.classList.toggle('is-selected', isSelected);
            sizeOption.setAttribute('aria-selected', isSelected ? 'true' : 'false');
          });
        }

        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
        }

        if (valueEl) {
          valueEl.textContent = sizeValue;
        }

        if (addButton) {
          const variantId = option.dataset.variantId || '';
          addButton.dataset.variantId = variantId;

          if (addButton.getAttribute('aria-busy') !== 'true') {
            addButton.disabled = !variantId;
          }
        }

        this.updateCardPrice(card, option);
        this.closeSizeMenu(true);
      }

      onDocumentClick(event) {
        if (!this.activeSizeControl) {
          return;
        }

        if (this.activeSizeControl.contains(event.target) || (this.activeSizeMenu && this.activeSizeMenu.contains(event.target))) {
          return;
        }

        this.closeSizeMenu(false);
      }

      onKeydown(event) {
        if (event.key !== 'Escape' || !this.activeSizeControl) {
          return;
        }

        this.closeSizeMenu(true);
      }

      updateCardPrice(card, option) {
        const priceEl = card.querySelector('[data-cart-upsell-price-current]');
        const comparePriceEl = card.querySelector('[data-cart-upsell-price-compare]');

        if (!priceEl) {
          return;
        }

        const price = this.parseMoney(option.dataset.variantPrice);
        const comparePrice = this.parseMoney(option.dataset.variantCompareAtPrice);
        const onSale = comparePrice > price;

        priceEl.textContent = this.formatMoney(price);
        priceEl.classList.toggle('cart-upsell__price--sale', onSale);

        if (!comparePriceEl) {
          return;
        }

        if (onSale) {
          comparePriceEl.textContent = this.formatMoney(comparePrice);
          comparePriceEl.hidden = false;
        } else {
          comparePriceEl.textContent = '';
          comparePriceEl.hidden = true;
        }
      }

      parseMoney(value) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
      }

      // tt-custom-cart-upsell
      // Mirrors the `money_without_trailing_zeros` filter used for the server-rendered
      // price (see sections/cart-drawer-upsell.liquid) so the price shown after picking
      // a size matches the price shown before picking one: whole amounts drop the
      // decimals entirely (225.00 -> 225) instead of always padding to 2dp.
      // window.theme.Currency / window.theme.settings.moneyFormat referenced previously
      // here don't exist anywhere in this theme, so that branch always silently fell
      // through to a hardcoded toFixed(2) - which is why "225.00" showed up on size
      // switch even though the initial card correctly read "225" via the `money` filter.
      /**
       * @param {number} cents
       */
      formatMoney(cents) {
        const amount = (Number(cents) || 0) / 100;
        const isWhole = Math.abs(amount - Math.round(amount)) < 0.005;
        const decimals = isWhole ? 0 : 2;

        const parts = amount.toFixed(decimals).split('.');
        let whole = parts[0] || '0';
        const fraction = parts[1];

        const useCommaDecimal = /comma_separator/.test(this.moneyFormat) && !/space_separator/.test(this.moneyFormat);
        const thousandsSeparator = useCommaDecimal
          ? '.'
          : /space_separator/.test(this.moneyFormat)
            ? ' '
            : /apostrophe_separator/.test(this.moneyFormat)
              ? "'"
              : ',';
        const decimalSeparator = useCommaDecimal ? ',' : '.';

        whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
        const formattedAmount = fraction ? `${whole}${decimalSeparator}${fraction}` : whole;

        return this.moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, formattedAmount);
      }
      // tt-custom-cart-upsell

      // tt-custom-cart-upsell
      async addItem(button) {
        const variantId = button.dataset.variantId;

        if (!variantId) {
          return;
        }

        this.closeSizeMenu(false);

        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        this.clearMessage();

        try {
          const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
            .map((element) => element.dataset.sectionId)
            .filter(Boolean);

          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', '1');
          formData.append('sections', sectionIds.join(','));

          const cartAddUrl = (typeof Theme !== 'undefined' && Theme.routes?.cart_add_url) || `${this.rootPath}cart/add.js`;

          const addResponse = await fetch(cartAddUrl, {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              Accept: 'application/json'
            },
            body: formData,
            credentials: 'same-origin'
          });

          const addPayload = await addResponse.json();

          if (!addResponse.ok || addPayload.status) {
            throw addPayload;
          }

          const cartResponse = await fetch(`${this.rootPath}cart.js`, { credentials: 'same-origin' });
          const cart = await cartResponse.json();

          this.removeAddedCard(button);

          // Same event name/shape as the theme's CartAddEvent (assets/events.js), built as a
          // plain CustomEvent since this inline javascript block can't import ES modules here.
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: cart,
                sourceId: variantId,
                data: {
                  source: 'cart-upsell',
                  itemCount: 1,
                  variantId,
                  sections: addPayload.sections
                }
              }
            })
          );
        } catch (error) {
          this.showMessage(error && (error.description || error.message) ? (error.description || error.message) : 'Unable to add item.');
        } finally {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }
      // tt-custom-cart-upsell

      hide() {
        this.closeSizeMenu(false);
        this.clearRetryTimer();
        this.refreshCartSnapshot = null;
        this.setAttribute('hidden', '');
        if (this.track) {
          this.track.innerHTML = '';
        }
        this.currentSignature = '';
        this.currentSeedId = '';
        this.currentState = '';
        this.clearMessage();
      }

      restorePanelState() {
        if (!this.details) {
          return;
        }

        try {
          const savedState = window.localStorage.getItem(this.panelStorageKey);
          if (savedState === 'collapsed') {
            this.details.open = false;
          } else if (savedState === 'expanded') {
            this.details.open = true;
          }
        } catch (error) {
        }
      }

      // tt-custom-cart-upsell
      savePanelState() {
        if (!this.details) {
          return;
        }

        try {
          window.localStorage.setItem(this.panelStorageKey, this.details.open ? 'expanded' : 'collapsed');
        } catch (error) {
        }

        // Safety net: if the panel is expanded but never finished loading (e.g. the
        // drawer-open/cart-update signal was missed), fetch recommendations now.
        if (this.details.open && this.track && !this.track.children.length && !this.loading) {
          this.refresh(true);
        }
      }
      // tt-custom-cart-upsell

      showMessage(message) {
        if (!this.message) {
          return;
        }

        this.message.textContent = message;
        this.message.classList.remove('hide');
      }

      clearMessage() {
        if (!this.message) {
          return;
        }

        this.message.textContent = '';
        this.message.classList.add('hide');
      }
    }

    CartUpsell.cache = CartUpsell.cache || new Map();
    customElements.define('cart-upsell', CartUpsell);
  }
